import React, { useMemo, useCallback } from 'react';
import { ActionButton, Content, Dialog, Heading, View } from '@adobe/react-spectrum';
import { useTranslation } from 'react-i18next';
import styles from './issue_template_picker_dialog.module.css';

type IssueTemplate = {
  id: string;
  name: string;
  taskTitle?: string;
  typeOfWork?: string;
  status?: string;
  priority?: string;
  reminder?: string;
  solver?: string;
  solverName?: string;
  acl?: string;
};

interface IIssueTemplatePickerDialogProps {
  templates: IssueTemplate[];
  onSelect: (template: IssueTemplate) => void;
  onClose: () => void;
}

const IssueTemplatePickerDialog: React.FC<IIssueTemplatePickerDialogProps> = ({ templates, onSelect, onClose }) => {
  const { t } = useTranslation('issue');
  const _t = useCallback((key: string) => t(`issue_template_picker.${key}`, { ns: 'issue' }), [t]);
  const groupedTemplates = useMemo(() => {
    const groups = new Map<string, IssueTemplate[]>();
    templates.forEach((template) => {
      const key = template.solverName || _t('unassigned');
      const existing = groups.get(key) || [];
      existing.push(template);
      groups.set(key, existing);
    });
    return Array.from(groups.entries());
  }, [_t, templates]);

  return (
    <Dialog
      width={{ base: '100%', M: '920px' }}
      isDismissable
      onDismiss={onClose}
      aria-label={_t('new_issue')}
      UNSAFE_style={{ background: '#f1f1f1' }}
    >
      <Heading>{_t('new_issue')}</Heading>
      <Content UNSAFE_className={styles.dialogContent}>
        <div className={styles.grid}>
          {groupedTemplates.map(([groupName, items]) => (
            <View key={groupName} UNSAFE_className={styles.column}>
              <View UNSAFE_className={styles.groupHeader}>{groupName}</View>
              <div className={styles.templateList}>
                {items.map((template) => (
                  <ActionButton
                    key={template.id}
                    onPress={() => onSelect(template)}
                    UNSAFE_className={styles.templateButton}
                  >
                    {template.taskTitle || template.name}
                  </ActionButton>
                ))}
              </div>
            </View>
          ))}
        </div>
      </Content>
    </Dialog>
  );
};

export default IssueTemplatePickerDialog;
